import { Button } from '@design-system';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { PageLink } from '@/components';
import {
  DownloadReportWithPdfOptions,
  OnboardingLayout,
  usePdfReportSections,
  useSlideSetup,
  useDownloadReportSlide,
} from '@/features/client-onboarding';
import { OnboardingFundingSources } from '@/pages/OnboardingFundingSources';
import { OnboardingFundingCoverage } from '@/pages/OnboardingFundingCoverage';
import { selectSessionAdvisor, selectSession, selectFeatureFlags } from '@client/model/selectors';
import { SessionType } from '@shared';
import { BookOpen, Edit } from 'lucide-react';
import { useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { Widget } from '@typeform/embed-react';
import { useEmailPdfGuide, usePdfReportDownload } from '@/features/pdf-report';
import { navigateToPrintReport } from '@/util/printUtils';

import styles from './OnboardingDownloadReport.module.css';

interface FeedbackSurveyProps {
  client: Client;
}

const FeedbackSurvey = ({ client }: FeedbackSurveyProps) => {
  const { clientEmail, clientFirstName, clientLastName, advisorId, surveys } = client;
  const surveyId = surveys?.length > 0 ? surveys[surveys.length - 1].surveyId : undefined;
  const formId = 'm4H0GpSY';
  return (
    <div data-testid="feedback-survey">
      <Widget
        id={formId}
        className={styles.surveyContainer}
        hidden={{
          consumer_email: clientEmail,
          consumer_first_name: clientFirstName,
          advisor_id: advisorId,
          survey_id: surveyId ?? 'xxxxx',
          organization_name: 'xxxxx',
          consumer_last_name: clientLastName,
        }}
      />
    </div>
  );
};

const PLAN_SUMMARY_GUIDE_URL =
  'https://userimg-assets.customeriomail.com/images/client-env-201612/1774554460133_how_to_understand_your_waterlily_plan_summary_01KMNV0CDXXEAVFC7QXEW93E5Q.pdf';

const PLAN_SUMMARY_GUIDE_SELF_GUIDED_URL =
  'https://userimg-assets.customeriomail.com/images/client-env-201612/1774563551076_how_to_understand_your_waterlily_plan_summary_self_guided_01KMP3NTE7X54BQJ33YRT5V3PZ.pdf';

export function OnboardingDownloadReport({ clientId }: ClientIdProps) {
  const { flags } = useFeatureFlags();
  const { client } = useDownloadReportSlide(clientId);
  const { downloadPdfReport, isDownloading } = usePdfReportDownload();
  const { sendOrOpen: handlePlanSummaryGuide, isSending: isSendingGuide } = useEmailPdfGuide();
  const advisor = useSelector(selectSessionAdvisor);
  const { sessionType } = useSelector(selectSession);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;
  const planSummaryGuideUrl =
    advisor.sendClientIntakeGuide === 'selfGuided'
      ? PLAN_SUMMARY_GUIDE_SELF_GUIDED_URL
      : PLAN_SUMMARY_GUIDE_URL;
  const { hydrate } = usePdfReportSections();
  const onboardingPdfReportOptionsEnabled = flags.onboardingPdfReportOptions === true;

  useLayoutEffect(() => {
    if (onboardingPdfReportOptionsEnabled) {
      hydrate();
    }
  }, [hydrate, onboardingPdfReportOptionsEnabled]);

  useSlideSetup({
    slidePage: OnboardingDownloadReport,
    nextButtonLabel: 'How did we do?',
    previousSlidePage: OnboardingFundingCoverage,
  });

  return (
    <OnboardingLayout>
      <div className="flex flex-col justify-center gap-6 py-6">
        <div className="flex w-full flex-wrap items-start justify-between gap-x-6 gap-y-4">
          <h1
            data-testid="download-report-header"
            className="text-balance text-center text-2xl font-bold text-gray-950 md:text-start md:text-4xl"
          >
            Download your report and share how we did
          </h1>
          <div className="grid w-full grid-cols-1 gap-2 *:w-full md:grid-cols-3 md:items-center">
            {!isD2C && (
              <PageLink
                to={OnboardingFundingSources}
                targetProps={{ clientId }}
              >
                <Button
                  variant="outline"
                  className="w-full"
                >
                  Update Financing Plan
                  <Edit
                    className="size-4 shrink-0"
                    aria-hidden
                  />
                </Button>
              </PageLink>
            )}
            <Button
              variant="outline"
              onClick={() => handlePlanSummaryGuide(planSummaryGuideUrl)}
              disabled={isSendingGuide}
            >
              {isSendingGuide ? 'Sending...' : 'How to Read Your Plan Summary'}
              {!isSendingGuide && (
                <BookOpen
                  className="size-4 shrink-0"
                  aria-hidden
                />
              )}
            </Button>
            <DownloadReportWithPdfOptions onDownload={flags.reactPdfReport ? downloadPdfReport : () => navigateToPrintReport(clientId)} loading={isDownloading} />
          </div>
        </div>
        <div className="hidden w-full flex-col gap-6 rounded-xl border pt-10 md:flex md:flex-row">
          <FeedbackSurvey client={client} />
        </div>
      </div>
    </OnboardingLayout>
  );
}
